/**
 * RiskDetailModal Component
 *
 * Modal for viewing risk cascade details and applying session-scoped overrides.
 * Shows the full risk resolution chain (account default → profile strategy →
 * profile playbook → session override) and allows the user to set/clear
 * session overrides with a required deviation note when any override exists.
 *
 * Persistence: writes directly to Dexie (session_drafts + note_drafts) in a
 * single transaction, replicating the updateTradingSetup() contract for the
 * 5 fields it touches (sessionStrategyOverrides, sessionPlaybookOverrides,
 * deviationNoteId, setupConfirmed, riskSnapshot).
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  TextArea,
  Alert,
  Card,
  CardHeader,
  CardBody,
  Switch,
  Input,
  Select,
  SelectItem,
} from '@enfomentis/ui';
import { db } from '@/lib/db';
import { accountService } from '@/services/AccountService';
import { noteDraftService } from '@/services/NoteDraftService';
import { generateUUID } from '@/utils/uuid';
import { isTombstoned } from '@/utils/tombstone';
import { computeStrategyRisk, computeEffectiveRisk } from '@/utils/riskComputationUtils';
import { formatRisk } from '@/utils/riskDisplayHelpers';
import { toDisplayValue, toStoredValue, defaultValueForType } from '@/components/Settings/Portfolio/Risk/riskHelpers';
import { markAsInstrumentedWriteContext } from '@/services/sync/SyncImportContext';
import { getSyncOperationService } from '@/services/sync/SyncOperationService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { buildDiffOps, buildSetOps, buildDeleteOp } from '@/services/sync/OperationBuilder';
import { softDeleteRecord } from '@/services/sync/SoftDeleteUtils';
import type { LogBatchOperationParams } from '@/services/sync/SyncOperationService';

// 3. Types
import type { AccountRisk, RiskSpec } from '@/types/schema/AccountRisk';
import type {
  SessionStrategyOverride,
  SessionPlaybookOverride,
  TradingSetupRiskSnapshot,
} from '@/types/schema/TradingSetupSnapshots';
import type { NoteDraft } from '@/types/transient/NoteDraft';

// =============================================================================
// Types
// =============================================================================

export interface RiskDetailModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback when save succeeds — parent updates React state from these values */
  onSave?: (updates: {
    sessionStrategyOverrides: SessionStrategyOverride[];
    sessionPlaybookOverrides: SessionPlaybookOverride[];
    setupConfirmed: boolean;
    riskSnapshot: TradingSetupRiskSnapshot | undefined;
  }) => void;
  /** Which risk row was tapped (null when modal is closed) */
  riskContext: { type: 'strategy' | 'playbook'; strategyId: string; playbookId?: string } | null;
  /** Canonical AccountRisk config for selected account */
  accountRiskConfig: AccountRisk;
  /** ID of the selected risk profile within accountRiskConfig */
  selectedRiskProfileId: string;
  /** Default account ID for display and note ownership */
  defaultAccountId: string;
  /** Current session strategy overrides */
  sessionStrategyOverrides: SessionStrategyOverride[];
  /** Current session playbook overrides */
  sessionPlaybookOverrides: SessionPlaybookOverride[];
  /** Session draft ID (for note ownerId and draft update) */
  sessionDraftId: string;
  /**
   * Callback from parent to compute the correct setupConfirmed/riskSnapshot state
   * for the given override updates. Uses canonicalizeSnapshot/snapshotsMatch internally.
   */
  computeConfirmationState?: (updates: {
    sessionStrategyOverrides: SessionStrategyOverride[];
    sessionPlaybookOverrides: SessionPlaybookOverride[];
  }) => { setupConfirmed: boolean; riskSnapshot: TradingSetupRiskSnapshot | undefined };
}

// =============================================================================
// Component
// =============================================================================

export const RiskDetailModal: React.FC<RiskDetailModalProps> = ({
  isOpen,
  onClose,
  onSave,
  riskContext,
  accountRiskConfig,
  selectedRiskProfileId,
  defaultAccountId,
  sessionStrategyOverrides,
  sessionPlaybookOverrides,
  sessionDraftId,
  computeConfirmationState,
}) => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  /** Whether the session override toggle is on for this context */
  const [overrideEnabled, setOverrideEnabled] = useState(false);

  /** Risk type for override input */
  const [riskType, setRiskType] = useState<'percentage' | 'fixed'>('percentage');

  /** Text input value for risk amount */
  const [riskInputValue, setRiskInputValue] = useState('');

  /** Deviation note textarea content */
  const [deviationText, setDeviationText] = useState('');

  /** Saving state */
  const [isSaving, setIsSaving] = useState(false);

  /** Save error message */
  const [saveError, setSaveError] = useState<string | null>(null);

  /** Account name loaded on open */
  const [accountName, setAccountName] = useState('');

  /** Deviation text loaded on open — used for no-op detection */
  const loadedDeviationTextRef = useRef('');

  // ---------------------------------------------------------------------------
  // Derived Values
  // ---------------------------------------------------------------------------

  /** Resolved risk profile from config */
  const resolvedProfile = useMemo(() => {
    return accountRiskConfig.riskProfiles.find((p) => p.id === selectedRiskProfileId);
  }, [accountRiskConfig, selectedRiskProfileId]);

  /** Currency from account's baseCurrency */
  const [currency, setCurrency] = useState('USD');

  /** Profile name for display */
  const profileName = resolvedProfile?.name ?? 'Unknown Profile';

  /** Profile cascade value for this context (before session overrides) */
  const profileCascadeRisk = useMemo(() => {
    if (!resolvedProfile || !riskContext) return undefined;
    if (riskContext.type === 'strategy' || !riskContext.playbookId) {
      return computeStrategyRisk(riskContext.strategyId, resolvedProfile);
    }
    return computeEffectiveRisk(riskContext.strategyId, riskContext.playbookId, resolvedProfile);
  }, [resolvedProfile, riskContext]);

  /** Account default risk */
  const accountDefaultRisk = resolvedProfile?.accountDefault;

  /** Profile strategy override for this context (if any) */
  const profileStrategyOverride = useMemo(() => {
    if (!resolvedProfile || !riskContext) return undefined;
    return resolvedProfile.strategyOverrides?.find(
      (o) => o.strategyId === riskContext.strategyId
    );
  }, [resolvedProfile, riskContext]);

  /** Profile playbook override for this context (playbook context only) */
  const profilePlaybookOverride = useMemo(() => {
    if (!resolvedProfile || !riskContext || riskContext.type !== 'playbook') return undefined;
    return resolvedProfile.playbookOverrides?.find(
      (o) => o.playbookId === riskContext.playbookId
    );
  }, [resolvedProfile, riskContext]);

  /** Existing session override for this context */
  const existingSessionOverride = useMemo((): RiskSpec | undefined => {
    if (!riskContext) return undefined;
    if (riskContext.type === 'strategy') {
      return sessionStrategyOverrides.find(
        (o) => o.strategyId === riskContext.strategyId
      )?.risk;
    }
    return sessionPlaybookOverrides.find(
      (o) => o.strategyId === riskContext.strategyId && o.playbookId === riskContext.playbookId
    )?.risk;
  }, [riskContext, sessionStrategyOverrides, sessionPlaybookOverrides]);

  /** Existing per-override deviation note ID for this context */
  const existingDeviationNoteId = useMemo((): string | undefined => {
    if (!riskContext) return undefined;
    if (riskContext.type === 'strategy') {
      return sessionStrategyOverrides.find(
        (o) => o.strategyId === riskContext.strategyId
      )?.deviationNoteId;
    }
    return sessionPlaybookOverrides.find(
      (o) => o.strategyId === riskContext.strategyId && o.playbookId === riskContext.playbookId
    )?.deviationNoteId;
  }, [riskContext, sessionStrategyOverrides, sessionPlaybookOverrides]);

  /** Compute updated override arrays based on current form state */
  const computeUpdatedOverrides = useCallback(() => {
    if (!riskContext) {
      return {
        updatedStrategyOverrides: sessionStrategyOverrides,
        updatedPlaybookOverrides: sessionPlaybookOverrides,
      };
    }

    const parsedInput = parseFloat(riskInputValue);
    const storedValue = !isNaN(parsedInput) ? toStoredValue(parsedInput, riskType) : 0;
    const newRisk: RiskSpec = { type: riskType, value: storedValue };

    let updatedStrategyOverrides = [...sessionStrategyOverrides];
    let updatedPlaybookOverrides = [...sessionPlaybookOverrides];

    if (riskContext.type === 'strategy') {
      // Remove existing override for this strategy
      updatedStrategyOverrides = updatedStrategyOverrides.filter(
        (o) => o.strategyId !== riskContext.strategyId
      );
      if (overrideEnabled) {
        updatedStrategyOverrides.push({ strategyId: riskContext.strategyId, risk: newRisk });
      }
    } else {
      // Remove existing override for this playbook
      updatedPlaybookOverrides = updatedPlaybookOverrides.filter(
        (o) => !(o.strategyId === riskContext.strategyId && o.playbookId === riskContext.playbookId)
      );
      if (overrideEnabled && riskContext.playbookId) {
        updatedPlaybookOverrides.push({
          strategyId: riskContext.strategyId,
          playbookId: riskContext.playbookId,
          risk: newRisk,
        });
      }
    }

    return { updatedStrategyOverrides, updatedPlaybookOverrides };
  }, [riskContext, riskInputValue, riskType, overrideEnabled, sessionStrategyOverrides, sessionPlaybookOverrides]);

  /** Whether any overrides exist after this edit */
  const hasAnyOverridesAfterEdit = useMemo(() => {
    const { updatedStrategyOverrides, updatedPlaybookOverrides } = computeUpdatedOverrides();
    return updatedStrategyOverrides.length > 0 || updatedPlaybookOverrides.length > 0;
  }, [computeUpdatedOverrides]);

  /** Validation: deviation note required when overrides exist */
  const isValid = useMemo(() => {
    if (hasAnyOverridesAfterEdit && deviationText.trim().length === 0) {
      return false;
    }
    if (overrideEnabled) {
      const parsedInput = parseFloat(riskInputValue);
      if (isNaN(parsedInput) || parsedInput <= 0) return false;
    }
    return true;
  }, [hasAnyOverridesAfterEdit, deviationText, overrideEnabled, riskInputValue]);

  /** Validation hint message */
  const validationHint = useMemo(() => {
    if (overrideEnabled) {
      const parsedInput = parseFloat(riskInputValue);
      if (isNaN(parsedInput) || parsedInput <= 0) {
        return 'Please enter a valid risk value greater than 0.';
      }
    }
    if (hasAnyOverridesAfterEdit && deviationText.trim().length === 0) {
      return 'A deviation note is required when session overrides are active.';
    }
    return null;
  }, [overrideEnabled, riskInputValue, hasAnyOverridesAfterEdit, deviationText]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  /**
   * Populate state when modal opens
   */
  useEffect(() => {
    if (!isOpen || !riskContext) return;

    // Load account name and currency
    accountService.get(defaultAccountId).then((account) => {
      if (account) {
        setAccountName(account.name);
        setCurrency(account.baseCurrency || 'USD');
      } else {
        setAccountName(defaultAccountId);
        setCurrency('USD');
      }
    });

    // Populate override fields from existing session override (if any)
    if (existingSessionOverride) {
      setOverrideEnabled(true);
      setRiskType(existingSessionOverride.type);
      setRiskInputValue(toDisplayValue(existingSessionOverride));
    } else if (profileCascadeRisk) {
      setOverrideEnabled(false);
      setRiskType(profileCascadeRisk.type);
      setRiskInputValue(toDisplayValue(profileCascadeRisk));
    } else {
      setOverrideEnabled(false);
      setRiskType('percentage');
      setRiskInputValue('1');
    }

    // Load existing deviation note text
    if (existingDeviationNoteId) {
      noteDraftService.get(existingDeviationNoteId).then((note) => {
        if (note && !isTombstoned(note)) {
          const text = note.text ?? '';
          setDeviationText(text);
          loadedDeviationTextRef.current = text;
        } else {
          setDeviationText('');
          loadedDeviationTextRef.current = '';
        }
      });
    } else {
      setDeviationText('');
      loadedDeviationTextRef.current = '';
    }

    // Reset common state
    setIsSaving(false);
    setSaveError(null);
  }, [isOpen, riskContext, existingSessionOverride, profileCascadeRisk, defaultAccountId, existingDeviationNoteId]);

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  const handleOverrideToggle = useCallback((enabled: boolean) => {
    setOverrideEnabled(enabled);
    setSaveError(null);
  }, []);

  const handleRiskTypeChange = useCallback((keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') return;
    const value = Array.from(keys)[0] as string | undefined;
    if (value === 'percentage' || value === 'fixed') {
      setRiskType(value);
      setRiskInputValue(toDisplayValue({ type: value, value: defaultValueForType(value) }));
      setSaveError(null);
    }
  }, []);

  const handleRiskInputChange = useCallback((value: string) => {
    setRiskInputValue(value);
    setSaveError(null);
  }, []);

  const handleDeviationTextChange = useCallback((value: string) => {
    setDeviationText(value);
    setSaveError(null);
  }, []);

  /**
   * Save handler: atomic Dexie transaction replicating updateTradingSetup() contract.
   * Includes no-op detection, sync-op logging for note tombstones, and
   * coherent setupConfirmed computation via computeConfirmationState callback.
   */
  const handleSave = useCallback(async () => {
    if (!riskContext || !isValid) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      // Step 1: Compute updated override arrays
      const { updatedStrategyOverrides, updatedPlaybookOverrides } = computeUpdatedOverrides();

      // Step 1b: No-op detection — canonical sorted comparison
      const sortStrategy = (arr: SessionStrategyOverride[]) =>
        [...arr].sort((a, b) => a.strategyId.localeCompare(b.strategyId));
      const sortPlaybook = (arr: SessionPlaybookOverride[]) =>
        [...arr].sort((a, b) =>
          a.strategyId === b.strategyId
            ? a.playbookId.localeCompare(b.playbookId)
            : a.strategyId.localeCompare(b.strategyId)
        );

      const strategyUnchanged =
        JSON.stringify(sortStrategy(updatedStrategyOverrides)) ===
        JSON.stringify(sortStrategy(sessionStrategyOverrides));
      const playbookUnchanged =
        JSON.stringify(sortPlaybook(updatedPlaybookOverrides)) ===
        JSON.stringify(sortPlaybook(sessionPlaybookOverrides));
      const deviationUnchanged =
        deviationText.trim() === loadedDeviationTextRef.current;

      if (strategyUnchanged && playbookUnchanged && deviationUnchanged) {
        // No effective change — close without writing
        onClose();
        return;
      }

      // Step 2: Compute hasAnyOverridesAfterEdit
      const hasOverrides = updatedStrategyOverrides.length > 0 || updatedPlaybookOverrides.length > 0;

      // Step 3: Load existing draft
      const existingDraft = await db.session_drafts.get(sessionDraftId);
      if (!existingDraft || isTombstoned(existingDraft)) {
        setSaveError('Session draft not found. Please refresh and try again.');
        return;
      }

      // Step 4: Build note data and determine deviationNoteId
      const now = new Date().toISOString();
      let newDeviationNoteId: string | undefined;
      const noteOps: LogBatchOperationParams[] = [];
      const deviationTextChanged = deviationText.trim() !== loadedDeviationTextRef.current;

      if (hasOverrides) {
        if (!deviationTextChanged && existingDeviationNoteId) {
          // Text unchanged and existing note exists — reuse its ID, skip note create
          newDeviationNoteId = existingDeviationNoteId;
        } else {
          // Text changed or no prior note — create new note with fresh ID
          newDeviationNoteId = generateUUID();
          const newNote: NoteDraft = {
            id: newDeviationNoteId,
            noteType: 'risk-deviation',
            ownerId: sessionDraftId,
            timestamp: now,
            captureContext: 'pre-trading',
            text: deviationText.trim(),
            createdAt: now,
            updatedAt: now,
          };
          noteOps.push(...buildSetOps('note_drafts', newDeviationNoteId, newNote as unknown as Record<string, unknown>));
        }
      }

      // Step 4b: Attach deviationNoteId to the correct override entry
      if (newDeviationNoteId) {
        if (riskContext.type === 'strategy') {
          const idx = updatedStrategyOverrides.findIndex((o) => o.strategyId === riskContext.strategyId);
          const entry = idx !== -1 ? updatedStrategyOverrides[idx] : undefined;
          if (entry) {
            updatedStrategyOverrides[idx] = { strategyId: entry.strategyId, risk: entry.risk, deviationNoteId: newDeviationNoteId };
          }
        } else if (riskContext.playbookId) {
          const idx = updatedPlaybookOverrides.findIndex((o) => o.strategyId === riskContext.strategyId && o.playbookId === riskContext.playbookId);
          const entry = idx !== -1 ? updatedPlaybookOverrides[idx] : undefined;
          if (entry) {
            updatedPlaybookOverrides[idx] = { strategyId: entry.strategyId, playbookId: entry.playbookId, risk: entry.risk, deviationNoteId: newDeviationNoteId };
          }
        }
      }

      // Step 4c: Compute confirmation state via parent callback
      const confirmState = computeConfirmationState?.({
        sessionStrategyOverrides: updatedStrategyOverrides,
        sessionPlaybookOverrides: updatedPlaybookOverrides,
      });
      const resolvedConfirmed = confirmState?.setupConfirmed ?? false;
      const resolvedRiskSnapshot = confirmState?.riskSnapshot;

      // Step 5: Build candidate draft and afterForDiff shadow
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const draft = { ...existingDraft } as any;
      const afterForDiff: Record<string, unknown> = { ...draft };

      // sessionStrategyOverrides: set if length > 0, else clear
      if (updatedStrategyOverrides.length > 0) {
        draft.sessionStrategyOverrides = updatedStrategyOverrides;
        afterForDiff['sessionStrategyOverrides'] = updatedStrategyOverrides;
      } else {
        delete draft.sessionStrategyOverrides;
        if (existingDraft.sessionStrategyOverrides !== undefined) {
          afterForDiff['sessionStrategyOverrides'] = null;
        }
      }

      // sessionPlaybookOverrides: same pattern
      if (updatedPlaybookOverrides.length > 0) {
        draft.sessionPlaybookOverrides = updatedPlaybookOverrides;
        afterForDiff['sessionPlaybookOverrides'] = updatedPlaybookOverrides;
      } else {
        delete draft.sessionPlaybookOverrides;
        if (existingDraft.sessionPlaybookOverrides !== undefined) {
          afterForDiff['sessionPlaybookOverrides'] = null;
        }
      }

      // deviationNoteId: deprecated session-level field — stop writing.
      // Per-override deviationNoteId is now carried on the override entries above.
      if (existingDraft.deviationNoteId !== undefined) {
        delete draft.deviationNoteId;
        afterForDiff['deviationNoteId'] = null;
      }

      // setupConfirmed: use computed value from parent's confirmation state
      draft.setupConfirmed = resolvedConfirmed;
      afterForDiff['setupConfirmed'] = resolvedConfirmed;

      // riskSnapshot: set if confirmed, else clear
      if (resolvedRiskSnapshot) {
        draft.riskSnapshot = resolvedRiskSnapshot;
        afterForDiff['riskSnapshot'] = resolvedRiskSnapshot;
      } else {
        delete draft.riskSnapshot;
        if (existingDraft.riskSnapshot !== undefined) {
          afterForDiff['riskSnapshot'] = null;
        }
      }

      // Step 6: Compute diff ops for session_drafts
      const draftOps: LogBatchOperationParams[] = buildDiffOps(
        'session_drafts',
        sessionDraftId,
        existingDraft as unknown as Record<string, unknown>,
        afterForDiff
      );

      // Step 6b: Update metadata.updatedAt (only if there are actual changes)
      if (draftOps.length > 0 || noteOps.length > 0) {
        draftOps.push({
          table: 'session_drafts' as const,
          recordId: sessionDraftId,
          op: 'set' as const,
          field: 'metadata.updatedAt',
          value: now,
        });
      }

      draft.metadata = {
        ...draft.metadata,
        updatedAt: now,
      };

      // Step 7: Combine all ops (note delete ops added inside transaction)
      const allOps: LogBatchOperationParams[] = [...draftOps, ...noteOps];

      // Step 8: Execute atomic transaction
      await db.transaction(
        'rw',
        [db.session_drafts, db.note_drafts, db.sync_operations, db.sync_state],
        async () => {
          markAsInstrumentedWriteContext();

          // Collect note IDs still referenced by any override entry.
          // Notes in this set are kept alive; unreferenced notes are tombstoned.
          const referencedNoteIds = new Set<string>();
          for (const o of updatedStrategyOverrides) {
            if (o.deviationNoteId) referencedNoteIds.add(o.deviationNoteId);
          }
          for (const o of updatedPlaybookOverrides) {
            if (o.deviationNoteId) referencedNoteIds.add(o.deviationNoteId);
          }
          if (newDeviationNoteId) referencedNoteIds.add(newDeviationNoteId);

          // Soft-delete unreferenced risk-deviation notes for this session
          const existingNotes = await db.note_drafts
            .where('[noteType+ownerId]')
            .equals(['risk-deviation', sessionDraftId])
            .toArray();
          for (const note of existingNotes) {
            if (!isTombstoned(note) && !referencedNoteIds.has(note.id)) {
              const result = await softDeleteRecord('note_drafts', note.id, { notify: false });
              if (result.deleted) {
                allOps.push(buildDeleteOp('note_drafts', note.id));
              }
            }
          }

          // Add new note if overrides exist and we're not reusing an existing note
          const reusingExistingNote = !deviationTextChanged && !!existingDeviationNoteId;
          if (hasOverrides && newDeviationNoteId && !reusingExistingNote) {
            const noteToWrite: NoteDraft = {
              id: newDeviationNoteId,
              noteType: 'risk-deviation',
              ownerId: sessionDraftId,
              timestamp: now,
              captureContext: 'pre-trading',
              text: deviationText.trim(),
              createdAt: now,
              updatedAt: now,
            };
            await db.note_drafts.put(noteToWrite);
          }

          // Write updated session draft
          await db.session_drafts.put(draft);

          // Log all sync ops
          if (allOps.length > 0) {
            await getSyncOperationService().logBatch(allOps);
          }
        }
      );

      // Step 9: Notify after transaction commits
      syncCoordinatorService.notifyChange('session_drafts', sessionDraftId);

      // Step 10: Call onSave with updated state (including confirmation state)
      onSave?.({
        sessionStrategyOverrides: updatedStrategyOverrides,
        sessionPlaybookOverrides: updatedPlaybookOverrides,
        setupConfirmed: resolvedConfirmed,
        riskSnapshot: resolvedRiskSnapshot,
      });
    } catch (error) {
      console.error('Failed to save risk override:', error);
      setSaveError(
        error instanceof Error
          ? error.message
          : 'Failed to save risk override. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  }, [riskContext, isValid, computeUpdatedOverrides, sessionDraftId, sessionStrategyOverrides, sessionPlaybookOverrides, deviationText, onClose, onSave, computeConfirmationState, existingDeviationNoteId]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!riskContext) return null;

  const contextLabel = riskContext.type === 'strategy' ? 'Strategy' : 'Playbook';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" placement="center">
      <ModalContent>
        <ModalHeader>Risk Detail — {contextLabel} Override</ModalHeader>

        <ModalBody className="space-y-4">
          {/* Error Alert */}
          {saveError && (
            <Alert color="danger" variant="flat">
              {saveError}
            </Alert>
          )}

          {/* Risk Cascade Display */}
          {resolvedProfile && (
            <Card>
              <CardHeader className="pb-0">
                <h4 className="text-sm font-medium text-text-primary">
                  Risk Cascade — {profileName}
                  {accountName && <span className="text-default-400 font-normal"> ({accountName})</span>}
                </h4>
              </CardHeader>
              <CardBody className="space-y-2">
                {/* Account Default */}
                {accountDefaultRisk && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-default-500">Account Default</span>
                    <span className="text-text-primary font-medium">
                      {formatRisk(accountDefaultRisk, currency)}
                    </span>
                  </div>
                )}

                {/* Profile Strategy Override (if any) */}
                {profileStrategyOverride && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-default-500">Profile Strategy Override</span>
                    <span className="text-text-primary font-medium">
                      {formatRisk(profileStrategyOverride.risk, currency)}
                    </span>
                  </div>
                )}

                {/* Profile Playbook Override (if any, playbook context only) */}
                {profilePlaybookOverride && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-default-500">Profile Playbook Override</span>
                    <span className="text-text-primary font-medium">
                      {formatRisk(profilePlaybookOverride.risk, currency)}
                    </span>
                  </div>
                )}

                {/* Resolved Effective Risk */}
                {profileCascadeRisk && (
                  <div className="flex items-center justify-between text-sm border-t border-default-200 pt-2 mt-1">
                    <span className="text-text-primary font-medium">Effective (Profile)</span>
                    <span className="text-primary font-semibold">
                      {formatRisk(profileCascadeRisk, currency)}
                    </span>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Session Override Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between w-full">
                <h4 className="text-sm font-medium text-text-primary">Session Override</h4>
                <Switch
                  isSelected={overrideEnabled}
                  onValueChange={handleOverrideToggle}
                  size="sm"
                  aria-label="Enable session override"
                />
              </div>
            </CardHeader>
            {overrideEnabled && (
              <CardBody className="pt-0 space-y-3">
                <div className="flex items-center gap-3">
                  <Select
                    label="Type"
                    selectedKeys={new Set([riskType])}
                    onSelectionChange={handleRiskTypeChange}
                    size="sm"
                    className="w-36"
                  >
                    <SelectItem key="percentage">Percentage</SelectItem>
                    <SelectItem key="fixed">Fixed</SelectItem>
                  </Select>
                  <Input
                    label={riskType === 'percentage' ? 'Value (%)' : `Value (${currency})`}
                    type="number"
                    value={riskInputValue}
                    onValueChange={handleRiskInputChange}
                    size="sm"
                    className="flex-1"
                    min="0"
                    step={riskType === 'percentage' ? '0.01' : '1'}
                    classNames={{
                      input: 'text-base', // Prevents mobile zoom on focus
                    }}
                  />
                </div>
              </CardBody>
            )}
          </Card>

          {/* Deviation Note Section — only visible when override is enabled */}
          {overrideEnabled && (
            <Card>
              <CardHeader className="pb-0">
                <h4 className="text-sm font-medium text-text-primary">
                  Deviation Note
                  {hasAnyOverridesAfterEdit && (
                    <span className="text-danger ml-1">*</span>
                  )}
                </h4>
              </CardHeader>
              <CardBody>
                <TextArea
                  placeholder="Explain why risk is being overridden for this session..."
                  value={deviationText}
                  onValueChange={handleDeviationTextChange}
                  minRows={2}
                  maxRows={5}
                  classNames={{
                    input: 'text-base', // Prevents mobile zoom on focus
                  }}
                />
              </CardBody>
            </Card>
          )}

          {/* Validation hint */}
          {validationHint && (
            <p className="text-xs text-text-tertiary">
              {validationHint}
            </p>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" onPress={onClose} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isDisabled={!isValid || isSaving}
            isLoading={isSaving}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RiskDetailModal;
